import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { appointmentsApi } from '../../api';
import { APPOINTMENT_TYPE_LABELS } from './appointmentConstants';

const emptyForm = {
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  appointment_date: '',
  appointment_time: '',
  duration_minutes: 30,
  type: 'callback',
  // Phase 6A — new structured type. Backend is the gate on dealer-type
  // compatibility, so we expose all six options here unconditionally.
  appointment_type: 'phone_callback',
  desired_vehicle_text: '',
  call_direction: 'outbound',
  notes: ''
};

// Phase 10B — `hideTrigger` lets the parent render its own button while we
// keep the dialog body here. The Calendar page now owns its primary CTA so
// the trigger button inside this dialog is suppressed when requested.
const CreateAppointmentDialog = ({ open, onOpenChange, onCreated, hideTrigger = false }) => {
  const [formData, setFormData] = useState(emptyForm);
  // Phase 6D — availability suggestions. Fetched when user has picked
  // an appointment type + date; clicking a slot fills appointment_time.
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const resetForm = () => setFormData(emptyForm);

  useEffect(() => {
    if (!formData.appointment_type || !formData.appointment_date) {
      setSlots([]);
      return;
    }
    let alive = true;
    setLoadingSlots(true);
    (async () => {
      try {
        const dayStart = new Date(`${formData.appointment_date}T00:00:00Z`).toISOString();
        const dayEnd = new Date(`${formData.appointment_date}T23:59:59Z`).toISOString();
        const res = await appointmentsApi.getAvailability({
          appointment_type: formData.appointment_type,
          date_from: dayStart,
          date_to: dayEnd,
          duration_minutes: formData.duration_minutes || 30,
        });
        if (alive) setSlots(Array.isArray(res) ? res : []);
      } catch {
        if (alive) setSlots([]);
      } finally {
        if (alive) setLoadingSlots(false);
      }
    })();
    return () => { alive = false; };
  }, [formData.appointment_type, formData.appointment_date, formData.duration_minutes]);

  const handleCreateAppointment = async () => {
    try {
      if (!formData.customer_name || !formData.customer_phone || !formData.appointment_date || !formData.appointment_time) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive'
        });
        return;
      }

      const appointmentData = {
        ...formData,
        appointment_date: new Date(formData.appointment_date).toISOString()
      };
      // Phase 6A — only send desired_vehicle_text when it's a test drive.
      if (formData.appointment_type !== 'test_drive') {
        delete appointmentData.desired_vehicle_text;
      }

      await appointmentsApi.create(appointmentData);

      toast.success(`Appointment scheduled for ${formData.customer_name}`);

      onOpenChange(false);
      resetForm();
      if (onCreated) onCreated();
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Failed to create appointment');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            data-testid="create-appointment-btn"
          >
            <Plus className="w-4 h-4" />
            New Appointment
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Appointment</DialogTitle>
          <DialogDescription>
            Schedule a callback or meeting with a customer
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="customer_name">Customer Name *</Label>
            <Input
              id="customer_name"
              placeholder="John Doe"
              value={formData.customer_name}
              onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_phone">Phone Number *</Label>
            <Input
              id="customer_phone"
              placeholder="+1234567890"
              value={formData.customer_phone}
              onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_email">Email (Optional)</Label>
            <Input
              id="customer_email"
              type="email"
              placeholder="john@example.com"
              value={formData.customer_email}
              onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="appointment_date">Date *</Label>
              <Input
                id="appointment_date"
                type="date"
                value={formData.appointment_date}
                onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointment_time">Time *</Label>
              <Input
                id="appointment_time"
                type="time"
                value={formData.appointment_time}
                onChange={(e) => setFormData({...formData, appointment_time: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="duration_minutes">Duration (minutes)</Label>
              <Input
                id="duration_minutes"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({...formData, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="callback">Callback</SelectItem>
                  <SelectItem value="sales_meeting">Sales Meeting</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="demo">Demo</SelectItem>
                  <SelectItem value="consultation">Consultation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="appointment_type">Appointment Type</Label>
            <Select
              value={formData.appointment_type}
              onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
            >
              <SelectTrigger data-testid="appointment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(APPOINTMENT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(slots.length > 0 || loadingSlots) && (
            <div className="text-xs">
              {loadingSlots ? (
                <div className="text-muted-foreground">Checking availability…</div>
              ) : (
                <>
                  <div className="text-muted-foreground mb-1">Suggested slots</div>
                  <div className="flex flex-wrap gap-2">
                    {slots.slice(0, 10).map((s) => {
                      const d = new Date(s.start_time);
                      const hh = String(d.getUTCHours()).padStart(2, '0');
                      const mm = String(d.getUTCMinutes()).padStart(2, '0');
                      return (
                        <button
                          key={s.start_time}
                          type="button"
                          className="px-2 py-1 rounded border hover:bg-muted"
                          onClick={() => setFormData((prev) => ({ ...prev, appointment_time: `${hh}:${mm}` }))}
                        >
                          {d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {formData.appointment_type === 'test_drive' && (
            <div className="space-y-2">
              <Label htmlFor="desired_vehicle_text">Desired Vehicle</Label>
              <Input
                id="desired_vehicle_text"
                placeholder="e.g. 2024 Toyota RAV4 XLE"
                value={formData.desired_vehicle_text}
                onChange={(e) => setFormData({ ...formData, desired_vehicle_text: e.target.value })}
                data-testid="desired-vehicle-input"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="call_direction">Call Direction</Label>
            <Select
              value={formData.call_direction}
              onValueChange={(value) => setFormData({...formData, call_direction: value})}
            >
              <SelectTrigger data-testid="appointment-call-direction-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outbound">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[hsl(196,43%,46%)]"></div>
                    Outbound (Blue)
                  </div>
                </SelectItem>
                <SelectItem value="inbound">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[hsl(160,58%,49%)]"></div>
                    Inbound (Green)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this appointment..."
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateAppointment}>
            Create Appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAppointmentDialog;
